import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Trophy, Lightbulb, Play, Plus } from "lucide-react";

/**
 * Lights Out Game
 * 
 * Rules:
 * - 5x5 Grid.
 * - Clicking a light toggles it and its 4 orthogonal neighbors.
 * - Goal: Turn all lights OFF.
 */
const LightsOutGame = () => {
    const navigate = useNavigate();

    // --- Constants ---
    const ROWS = 5;
    const COLS = 5;

    // --- State ---
    const [board, setBoard] = useState(Array(ROWS).fill(null).map(() => Array(COLS).fill(false)));
    // Track the net moves needed to solve the board. 
    // True at (r,c) means clicking (r,c) is part of the solution set.
    const [solution, setSolution] = useState(Array(ROWS).fill(null).map(() => Array(COLS).fill(false))); 
    const [isWon, setIsWon] = useState(false);
    const [moves, setMoves] = useState(0);
    const [isSolving, setIsSolving] = useState(false);
    
    // Use a ref to keep track of solving state during async operations if needed,
    // though state is sufficient for disabling UI.
    // Use a ref to keep track of solving state during async operations if needed,
    // though state is sufficient for disabling UI.
    // Use a ref to keep track of solving state during async operations if needed,
    // though state is sufficient for disabling UI.
    const solvingRef = useRef(false);
    // Store the state before solving allowed us to replay
    const preSolveState = useRef(null);

    // Initial state storage for "Reset" functionality
    const [initialBoard, setInitialBoard] = useState(null);
    const [initialSolution, setInitialSolution] = useState(null);

    // --- Logic ---

    // Toggle a single cell and its neighbors
    // Also updates the solution tracker if provided
    const toggleCell = (boardState, r, c, solutionState = null) => {
        const newBoard = boardState.map(row => [...row]);
        const directions = [[0,0], [0,1], [0,-1], [1,0], [-1,0]]; // Center, Right, Left, Down, Up
        
        directions.forEach(([dr, dc]) => {
            const newR = r + dr;
            const newC = c + dc;
            if (newR >= 0 && newR < ROWS && newC >= 0 && newC < COLS) {
                newBoard[newR][newC] = !newBoard[newR][newC];
            }
        });

        // Update solution state: simply toggle the bit at (r,c)
        // because doing a move twice cancels it out.
        let newSolution = null;
        if (solutionState) {
            newSolution = solutionState.map(row => [...row]);
            newSolution[r][c] = !newSolution[r][c];
        }

        return { newBoard, newSolution };
    };

    const handleCellClick = (r, c) => {
        if (isWon || isSolving) return;
        
        const { newBoard, newSolution } = toggleCell(board, r, c, solution);
        setBoard(newBoard);
        setSolution(newSolution);
        setMoves(prev => prev + 1);
        
        // Check Win
        const allOff = newBoard.every(row => row.every(cell => !cell));
        if (allOff) {
            setIsWon(true);
        }
    };

    /**
     * Show Solution
     * Finds all pending moves in the 'solution' state and executes them one by one.
     */
    /**
     * Show Solution
     * Finds all pending moves in the 'solution' state and executes them one by one.
     */
    /**
     * Show Solution
     * Finds all pending moves in the 'solution' state and executes them one by one.
     * After showing the solution, it reverts to the original state.
     */
    const handleShowSolution = async () => {
        if (isSolving) return; 
        
        setIsSolving(true);
        solvingRef.current = true;

        // Strict Deep Snapshot of current state
        const snapshot = JSON.parse(JSON.stringify({
           board: board,
           solution: solution,
           moves: moves
        }));

        // Collect all needed moves from the START solution
        const neededMoves = [];
        for (let r = 0; r < ROWS; r++) {
            for (let c = 0; c < COLS; c++) {
                if (solution[r][c]) {
                     neededMoves.push([r, c]);
                }
            }
        }

        // Execute moves with delay
        for (let i = 0; i < neededMoves.length; i++) {
            if (!solvingRef.current) break; // Safety break
            
            const [r, c] = neededMoves[i];
            
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1000ms delay
            
            setBoard(prevBoard => {
                const { newBoard } = toggleCell(prevBoard, r, c); 
                return newBoard;
            });
            // We don't technically need to update 'solution' state for the visual preview since we revert,
            // but it keeps the internal logic consistent during the animation.
            setSolution(prevSol => {
                const newSol = prevSol.map(row => [...row]);
                newSol[r][c] = !newSol[r][c]; 
                return newSol;
            });
            setMoves(prev => prev + 1);
        }

        // Wait a moment for user to see the solved (empty) board
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Restore state
        setBoard(snapshot.board);
        setSolution(snapshot.solution);
        setMoves(snapshot.moves);
        
        setIsSolving(false);
        solvingRef.current = false;
    };
    


    // Effect to check win condition whenever board changes
    // Effect to check win condition whenever board changes
    useEffect(() => {
        // Only check win if NOT solving (user played)
        if (!isSolving) {
            const allOff = board.every(row => row.every(cell => !cell));
            if (allOff) {
                setIsWon(true);
            }
        }
    }, [board, isSolving]);


    /**
     * Initialize a Solvable Board
     */
    /**
     * Initialize a Solvable Board
     */
    const startNewGame = () => {
        setIsSolving(false);
        solvingRef.current = false;
        preSolveState.current = null;
        
        let newBoard = Array(ROWS).fill(null).map(() => Array(COLS).fill(false));
        let newSolution = Array(ROWS).fill(null).map(() => Array(COLS).fill(false)); 
        
        // Scramble
        // We do 10-30 random "clicks" to set up the board.
        const complexity = Math.floor(Math.random() * 20) + 10; 
        
        for (let i = 0; i < complexity; i++) {
            const r = Math.floor(Math.random() * ROWS);
            const c = Math.floor(Math.random() * COLS);
            const res = toggleCell(newBoard, r, c, newSolution);
            newBoard = res.newBoard;
            newSolution = res.newSolution;
        }

        // Edge case check
        const allOff = newBoard.every(row => row.every(cell => !cell));
        if (allOff) {
             const r = Math.floor(Math.random() * ROWS);
             const c = Math.floor(Math.random() * COLS);
             const res = toggleCell(newBoard, r, c, newSolution);
             newBoard = res.newBoard;
             newSolution = res.newSolution;
        }

        setBoard(newBoard);
        setSolution(newSolution);

        // Save initial state for Reset
        // Use JSON parse/stringify for deep copy to be safe, though arrays of primitives are simpler
        setInitialBoard(JSON.parse(JSON.stringify(newBoard)));
        setInitialSolution(JSON.parse(JSON.stringify(newSolution)));

        setMoves(0);
        setIsWon(false);
    };

    /**
     * Reset the current game to its initial scrambled state
     */
    const resetGame = () => {
        if (!initialBoard || isSolving) return;
        
        setIsSolving(false);
        solvingRef.current = false; // Stop any ongoing solve animation
        
        setBoard(JSON.parse(JSON.stringify(initialBoard)));
        setSolution(JSON.parse(JSON.stringify(initialSolution)));
        setMoves(0);
        setIsWon(false);
    };

    // Start game on mount
    useEffect(() => {
        startNewGame();
    }, []);


    return (
        <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center p-4">
            {/* Header */}
            <div className="absolute top-4 left-4">
                <button
                    onClick={() => navigate("/games")}
                    className="flex items-center text-gray-400 hover:text-white transition-colors"
                >
                    <ArrowLeft className="h-6 w-6 mr-2" />
                    Back to Games
                </button>
            </div>

            <div className="max-w-md w-full">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-white mb-2 flex items-center justify-center">
                        <Lightbulb className="w-8 h-8 mr-3 text-yellow-400" />
                        Lights Out
                    </h1>
                    <p className="text-gray-400">Turn all the lights off to win!</p>
                </div>

                {/* Game Board */}
                <div className="bg-gray-800 p-6 rounded-2xl shadow-2xl border border-gray-700">
                    {isWon ? (
                        <div className="text-center py-10 animate-fade-in">
                            <Trophy className="w-20 h-20 text-yellow-400 mx-auto mb-4" />
                            <h2 className="text-3xl font-bold text-white mb-2">You Won!</h2>
                            <p className="text-gray-400 mb-6">It took you {moves} moves.</p>
                            <div className="flex flex-col space-y-3 justify-center items-center">
                                <button
                                    onClick={startNewGame}
                                    className="px-8 py-3 bg-yellow-500 text-gray-900 font-bold rounded-full hover:bg-yellow-400 transition-transform active:scale-95 shadow-lg flex items-center w-48 justify-center"
                                >
                                    <Plus className="w-5 h-5 mr-2" />
                                    New Game
                                </button>
                                <button
                                    onClick={resetGame}
                                    className="px-8 py-2 bg-gray-700 text-white font-semibold rounded-full hover:bg-gray-600 transition-transform active:scale-95 shadow-lg flex items-center w-48 justify-center border border-gray-600"
                                >
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Replay Game
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-5 gap-3 sm:gap-4 mb-6">
                            {board.map((row, r) => (
                                row.map((isOn, c) => (
                                    <button
                                        key={`${r}-${c}`}
                                        onClick={() => handleCellClick(r, c)}
                                        disabled={isSolving}
                                        className={`
                                            w-12 h-12 sm:w-14 sm:h-14 rounded-lg shadow-lg transition-all duration-300 transform active:scale-95
                                            ${isOn 
                                                ? "bg-yellow-400 shadow-[0_0_15px_rgba(250,204,21,0.6)] border-2 border-yellow-200" 
                                                : "bg-gray-700 hover:bg-gray-600 border-2 border-gray-600"}
                                            ${isSolving ? "cursor-wait opacity-80" : ""}
                                        `}
                                        aria-label={`Light at row ${r}, col ${c} is ${isOn ? 'on' : 'off'}`}
                                    />
                                ))
                            ))}
                        </div>
                    )}
                    
                    {!isWon && (
                        <div className="flex justify-between items-center text-gray-400 px-2 border-t border-gray-700 pt-4 mt-2">
                             <div className="font-mono text-lg">Moves: <span className="text-white">{moves}</span></div>
                             <div className="flex space-x-3">
                                <button 
                                    onClick={handleShowSolution}
                                    disabled={isSolving}
                                    className={`flex items-center transition-colors text-sm px-3 py-1 rounded-full border border-gray-600 hover:bg-gray-700 hover:text-white ${isSolving ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    title="Auto-solve the game"
                                >
                                    <Play className="w-4 h-4 mr-1" /> Solve
                                </button>
                                <button 
                                    onClick={resetGame}
                                    disabled={isSolving}
                                    className={`flex items-center hover:text-white transition-colors text-sm ${isSolving ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <RefreshCw className="w-4 h-4 mr-1" /> Reset
                                </button>
                                <button 
                                    onClick={startNewGame}
                                    disabled={isSolving}
                                    className={`flex items-center hover:text-white transition-colors text-sm ${isSolving ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <Plus className="w-4 h-4 mr-1" /> New
                                </button>
                             </div>
                        </div>
                    )}
                </div>
                
                {/* Instructions */}
                {!isWon && (
                    <div className="mt-8 text-center text-gray-500 text-sm">
                        Click a light to toggle it and its neighbors.<br/>Green/Yellow = ON, Gray = OFF.
                    </div>
                )}
            </div>
        </div>
    );
};

export default LightsOutGame;
